// servizi/account.ts — chi usa l'app: ospite o registrato (Google/Apple). Tiene la sessione sul telefono,
// legge profilo, piano e quota dal server, e aggiorna i filtri disponibili in base al piano.
// Il piano lo decide sempre il server: qui lo si legge e basta.
import { useEffect, useState, useSyncExternalStore } from "react";
import { AVATAR_PREDEFINITO, eAvatar } from "../../../condiviso/avatar.ts";
import { VERSIONE_VOLTO } from "../../../condiviso/volto.ts";
import type { Scelte } from "../../../condiviso/scelte.ts";
import { coloreSalvato, eIdColore, salvaColore, type IdColore } from "../temi/colori.ts";
import { ErroreRete, impostaSessione, impostaSuSessioneScaduta, richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { leggiPreferenza, salvaPreferenza } from "./preferenze.ts";
import { impostaProfiloFiltri } from "./filtri.ts";
import { IMP_APP, PIATTAFORMA, PREFERENZE } from "../costanti.ts";
import { eBooleano, leggiImpostazione } from "./impostazioni.ts";

export type Piano = "base" | "silver" | "gold";
export interface Quota {
  illimitate: boolean;
  limite: number;
  usate: number;
  inVolo: number;
  restanti: number;
  premi: number;
  premiRestanti: number;
  /** C'è ancora un premio video disponibile oggi (il codice per il video si chiede con codicePremio). */
  premiPossibili: boolean;
}
export interface Io {
  id: number;
  nome: string;
  stato: "attivo" | "sospeso";
  piano: Piano;
  pianoScade: string | null;
  pianoFonte: "revenuecat" | "amministratore" | null;
  identita: { fornitore: "google" | "apple"; email: string | null }[];
  creatoIl: string;
  idAcquisti: string;
  quota: Quota;
  // parte social (v006)
  nomeUtente: string | null;
  linkAttivo: boolean;
  /** v033: accetta sfide da chiunque (Disponibili ora, Wall of Fame); assente con un server vecchio */
  apertoATutti?: boolean;
  crediti: number;
  inClassifica: boolean;
  paese: string | null;
  paeseCambiatoIl: string | null;
  puoClassifica: boolean;
  puoLink: boolean;
  posseduti: string[];
  /** Modalità di prova (v008): tester con prova.attiva accesa. */
  tester?: boolean;
  /** Avatar scelto (v019), null = non ancora scelto. */
  avatar?: number | null;
  /** v021: colore primario scelto con l'account (vale su tutti i telefoni). */
  colore?: string | null;
  /** v021: sul sito può comprare un abbonamento (18 anni compiuti). */
  puoAcquistoSito?: boolean;
  /** v021: versione della spiegazione dell'analisi del volto accettata con l'account, null = mai. */
  voltoVersione?: number | null;
  /** v040: il consenso all'analisi del volto è stato ritirato (scrivendoci): il telefono non rimanda il suo. */
  voltoRitirato?: boolean;
  /** v040: «Le tue scelte» salvate sull'account (null = mai scelte; assenti con un server vecchio). */
  scelte?: Scelte | null;
  /** v041: spiegazioni e tutorial già visti (con l'account: su tutti i telefoni e anche in incognito) */
  visti?: Record<string, boolean> | null;
}

export type StatoAccount =
  | { tipo: "ospite" }
  | { tipo: "caricamento" }
  | { tipo: "registrazione"; token: string; email: string | null; etaMinima: number }
  | { tipo: "registrato"; io: Io };

// Sul sito la sessione sta in un cookie HttpOnly (JavaScript non la vede: una pagina compromessa non la ruba);
// qui si ricorda solo "sono connesso". Nelle app native il token sta nelle preferenze dell'app.
const WEB = PIATTAFORMA === "web";
if (WEB && leggiPreferenza(PREFERENZE.sessione)) salvaPreferenza(PREFERENZE.sessione, ""); // vecchie versioni

let stato: StatoAccount = { tipo: connesso() ? "caricamento" : "ospite" };
/** v021: la spiegazione del volto è stata accettata in questa pagina senza account caricato: va salvata quando arriva. */
let voltoDaSalvare = false;
const ascoltatori = new Set<() => void>();
function imposta(s: StatoAccount): void {
  stato = s;
  const piano = s.tipo === "registrato" ? s.io.piano : "base";
  // Filtri esclusivi comprati con i crediti: si possono usare (e finiscono nei cambi automatici in partita).
  impostaProfiloFiltri({
    piano,
    posseduti: new Set(s.tipo === "registrato" ? (s.io.posseduti ?? []) : []),
    tester: s.tipo === "registrato" && s.io.tester === true,
    goldEsclusivi: leggiImpostazione(IMP_APP.goldFiltriEsclusivi, true, eBooleano),
  });
  // v021: il colore scelto con l'account vale anche su questo telefono
  if (s.tipo === "registrato" && eIdColore(s.io.colore) && s.io.colore !== coloreSalvato()) salvaColore(s.io.colore);
  if (s.tipo === "registrato") {
    allineaScelte(s.io);
    allineaVisti(s.io);
  }
  ascoltatori.forEach((a) => a());
  // v021: «Ho capito» dato mentre l'account si caricava (o da ospite, poi entrato): si salva anche sull'account.
  // v040: anche se dato su questo telefono in un'altra visita (da ospite, o il salvataggio sul server non era
  // riuscito): prima la schermata ricompariva a ogni partita. Non se il consenso è stato ritirato scrivendoci.
  if (s.tipo === "registrato" && !voltoDaSalvare && !s.io.voltoRitirato && Number(s.io.voltoVersione ?? 0) < VERSIONE_VOLTO && Number(leggiPreferenza(PREFERENZE.volto)) >= VERSIONE_VOLTO)
    voltoDaSalvare = true;
  if (s.tipo === "registrato" && s.io.voltoRitirato && !voltoDaSalvare) salvaPreferenza(PREFERENZE.volto, "0");
  if (s.tipo === "registrato" && voltoDaSalvare && Number(s.io.voltoVersione ?? 0) < VERSIONE_VOLTO) {
    voltoDaSalvare = false;
    s.io.voltoVersione = VERSIONE_VOLTO; // subito, senza aspettare il profilo di nuovo
    void richiesta("/api/v1/io", { metodo: "PATCH", corpo: { voltoVersione: VERSIONE_VOLTO } }).catch(() => undefined);
  }
}

/**
 * v040: «Le tue scelte». Con l'account valgono quelle dell'account (uguali su tutti i telefoni): si copiano sul
 * telefono, dove le leggono la partita e la fine partita. Se l'account non le ha ancora ma il telefono sì (scelte da
 * ospite, poi registrato), si salvano sull'account.
 */
function allineaScelte(io: Io): void {
  if (io.scelte === undefined) return; // server vecchio
  if (io.scelte) {
    salvaPreferenza(PREFERENZE.foto, io.scelte.foto ? "si" : "no");
    salvaPreferenza(PREFERENZE.clip, io.scelte.video ? "si" : "no");
    salvaPreferenza(PREFERENZE.noi, io.scelte.noi ? "si" : "no");
    salvaPreferenza(PREFERENZE.scelteFatte, "1");
    return;
  }
  const clip = leggiPreferenza(PREFERENZE.clip);
  if (leggiPreferenza(PREFERENZE.scelteFatte) !== "1" && clip !== "si" && clip !== "no") return;
  const foto = leggiPreferenza(PREFERENZE.foto);
  const sc: Scelte = {
    foto: foto ? foto === "si" : clip === "si",
    video: leggiPreferenza(PREFERENZE.clip) === "si",
    noi: leggiPreferenza(PREFERENZE.noi) === "si",
  };
  io.scelte = sc;
  void richiesta("/api/v1/io", { metodo: "PATCH", corpo: { scelte: sc } }).catch(() => undefined);
}

// --- v041: spiegazioni e tutorial visti una volta, per l'account -----------------------------------------------
// Sul telefono: ld.spiegazioni ({torneo, gruppo}) e ld.tutorial ({1v1, torneo, gruppo}). Con l'account si uniscono a
// quelli salvati sull'account (chiavi «s.torneo», «t.1v1»…): visti su un telefono, visti su tutti.
function leggiOggetto(chiave: string): Record<string, boolean> {
  try {
    const v = JSON.parse(leggiPreferenza(chiave) ?? "{}") as unknown;
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}
function vistiQui(): Record<string, boolean> {
  const r: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(leggiOggetto(PREFERENZE.spiegazioniViste))) if (v === true) r[`s.${k}`] = true;
  for (const [k, v] of Object.entries(leggiOggetto(PREFERENZE.tutorial))) if (v === true) r[`t.${k}`] = true;
  return r;
}
function allineaVisti(io: Io): void {
  if (io.visti === undefined) return; // server vecchio
  const qui = vistiQui();
  const conto = io.visti ?? {};
  const tutti = { ...conto, ...qui };
  const s: Record<string, boolean> = {};
  const t: Record<string, boolean> = {};
  for (const k of Object.keys(tutti)) {
    if (k.startsWith("s.")) s[k.slice(2)] = true;
    if (k.startsWith("t.")) t[k.slice(2)] = true;
  }
  salvaPreferenza(PREFERENZE.spiegazioniViste, JSON.stringify(s));
  salvaPreferenza(PREFERENZE.tutorial, JSON.stringify(t));
  if (Object.keys(tutti).some((k) => conto[k] !== true)) {
    io.visti = tutti;
    void richiesta("/api/v1/io", { metodo: "PATCH", corpo: { visti: tutti } }).catch(() => undefined);
  }
}
/** v041: una spiegazione o un tutorial appena visto: con l'account si salva anche lì. */
export function vistiCambiati(): void {
  if (stato.tipo === "registrato") allineaVisti(stato.io);
}

/** v040: cambia subito un pezzo del profilo in memoria (il server conferma dopo): le altre schede lo vedono già. */
export function aggiornaIoLocale(parziale: Partial<Io>): void {
  if (stato.tipo !== "registrato") return;
  imposta({ ...stato, io: { ...stato.io, ...parziale } });
}

/** v040: salva «Le tue scelte» sul telefono e, con l'account, sull'account. */
export async function salvaScelteAccount(sc: Scelte): Promise<void> {
  salvaPreferenza(PREFERENZE.foto, sc.foto ? "si" : "no");
  salvaPreferenza(PREFERENZE.clip, sc.video ? "si" : "no");
  salvaPreferenza(PREFERENZE.noi, sc.noi ? "si" : "no");
  salvaPreferenza(PREFERENZE.scelteFatte, "1");
  if (stato.tipo === "registrato") {
    stato.io.scelte = sc;
    imposta({ ...stato, io: { ...stato.io, scelte: sc } });
    await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { scelte: sc } });
  } else ascoltatori.forEach((a) => a());
}

function leggiSessione(): string | null {
  if (WEB) return null;
  const v = leggiPreferenza(PREFERENZE.sessione);
  return v && /^[A-Za-z0-9_-]{43}$/.test(v) ? v : null;
}
function connesso(): boolean {
  return WEB ? leggiPreferenza(PREFERENZE.connesso) === "1" : leggiSessione() !== null;
}
/** `token`: quello delle app native; sul sito arriva null (il cookie l'ha messo il server) e vale `entrato`. */
function salvaSessione(token: string | null, entrato = token !== null): void {
  if (WEB) salvaPreferenza(PREFERENZE.connesso, entrato ? "1" : "");
  else salvaPreferenza(PREFERENZE.sessione, token ?? "");
}

impostaSessione(leggiSessione, connesso);
impostaSuSessioneScaduta(() => {
  salvaSessione(null);
  imposta({ tipo: "ospite" });
});

export function useAccount(): StatoAccount {
  return useSyncExternalStore(
    (f) => {
      ascoltatori.add(f);
      return () => ascoltatori.delete(f);
    },
    () => stato,
  );
}
export const statoAccount = (): StatoAccount => stato;
export const piattaforma = (): "web" | "android" | "ios" => PIATTAFORMA;

/** Rilegge profilo, piano e quota (all'avvio, dopo un acquisto, dopo una partita). */
export async function aggiornaIo(): Promise<void> {
  if (!connesso()) {
    if (stato.tipo !== "registrazione") imposta({ tipo: "ospite" });
    return;
  }
  try {
    const io = await richiesta<Io>("/api/v1/io");
    imposta({ tipo: "registrato", io });
    // v038: un avatar o un colore scelti quando il salvataggio sul conto non è riuscito: si rimandano adesso
    if (leggiPreferenza(PREFERENZE.aspettoInSospeso) === "1" && !aspettoInCorso) void rimandaAspetto(io);
    // v041: avatar scelto prima di registrarsi (matita della registrazione): l'account nuovo non ne ha ancora uno
    else if ((io.avatar === null || io.avatar === undefined) && eAvatar(Number(leggiPreferenza(PREFERENZE.avatar))) && !aspettoInCorso) void rimandaAspetto(io);
  } catch (e) {
    // 401: sessione finita (già gestito); rete giù: si resta come si era, si riproverà.
    if (e instanceof ErroreRete && e.stato === 401) return;
    if (stato.tipo === "caricamento") imposta({ tipo: "ospite" });
    throw e;
  }
}

/** Nonce emesso dal server per UN tentativo di accesso: lega il token di Google/Apple a questa richiesta. */
export async function nuovoNonce(): Promise<string> {
  const r = await richiesta<{ nonce: string }>("/api/v1/accesso/nonce", { metodo: "POST" });
  return r.nonce;
}

/** Con il token d'identità di Google/Apple: si entra, oppure (la prima volta) si passa alla registrazione. */
export async function accedi(fornitore: "google" | "apple", tokenIdentita: string, nonce: string): Promise<void> {
  const r = await richiesta<{ sessione?: string | null; registrazione?: { token: string; email: string | null; etaMinima: number } }>("/api/v1/accesso", {
    metodo: "POST",
    corpo: { fornitore, tokenIdentita, nonce, piattaforma: PIATTAFORMA },
  });
  if (r.sessione !== undefined) {
    salvaSessione(r.sessione, true);
    await aggiornaIo();
  } else if (r.registrazione) imposta({ tipo: "registrazione", ...r.registrazione });
}

export async function registra(nome: string, dataNascita: string): Promise<void> {
  if (stato.tipo !== "registrazione") throw new ErroreRete("registrazione_scaduta");
  try {
    const r = await richiesta<{ sessione: string | null }>("/api/v1/registrazione", {
      metodo: "POST",
      corpo: { token: stato.token, nome, dataNascita, piattaforma: PIATTAFORMA },
    });
    salvaSessione(r.sessione, true);
    salvaPreferenza(PREFERENZE.nascita, dataNascita);
    salvaPreferenza(PREFERENZE.nome, nome); // lo stesso nome propone «Sfida qualcuno»
    await aggiornaIo();
  } catch (e) {
    // Età insufficiente o registrazione scaduta: si torna ospite (per riprovare serve rientrare con Google/Apple).
    if (e instanceof ErroreRete && ["eta_insufficiente", "registrazione_scaduta"].includes(e.codice)) imposta({ tipo: "ospite" });
    throw e;
  }
}

export function annullaRegistrazione(): void {
  if (stato.tipo === "registrazione") imposta({ tipo: "ospite" });
}

/** Collega Google o Apple all'account aperto (token d'identità nuovo e nonce del server). */
export async function collegaIdentita(fornitore: "google" | "apple", tokenIdentita: string, nonce: string): Promise<void> {
  await richiesta("/api/v1/io/identita", { metodo: "POST", corpo: { fornitore, tokenIdentita, nonce } });
  await aggiornaIo();
}

export async function scollegaIdentita(fornitore: "google" | "apple"): Promise<void> {
  await richiesta(`/api/v1/io/identita/${fornitore}`, { metodo: "DELETE" });
  await aggiornaIo();
}

export async function cambiaNome(nome: string): Promise<void> {
  await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { nome } });
  await aggiornaIo();
}

/** Avatar (v019): sul telefono sempre (anche per gli ospiti), sul server per chi ha l'account. */
export async function scegliAvatar(n: number): Promise<void> {
  if (!eAvatar(n)) return;
  salvaPreferenza(PREFERENZE.avatar, String(n));
  avvisaAvatar();
  if (stato.tipo === "registrato") {
    await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { avatar: n } });
    await aggiornaIo();
  }
}

/**
 * v033: avatar e colore insieme, con UNA richiesta (prima erano tre: avatar, profilo, colore; se una non andava il
 * messaggio era sempre lo stesso). Sul telefono si salvano subito; sul server per chi ha l'account.
 */
/** v038: un salvataggio dell'avatar è in corso (non si rimanda una seconda volta). */
let aspettoInCorso = false;
async function rimandaAspetto(io: Io): Promise<void> {
  aspettoInCorso = true;
  const avatar = Number(leggiPreferenza(PREFERENZE.avatar));
  const colore = leggiPreferenza(PREFERENZE.colore) as IdColore | null;
  try {
    if (colore || eAvatar(avatar)) {
      await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { ...(colore ? { colore } : {}), ...(eAvatar(avatar) ? { avatar } : {}) } });
      imposta({ tipo: "registrato", io: { ...io, ...(colore ? { colore } : {}), ...(eAvatar(avatar) ? { avatar } : {}) } });
    }
    salvaPreferenza(PREFERENZE.aspettoInSospeso, "0");
  } catch {
    // si riproverà alla prossima apertura
  } finally {
    aspettoInCorso = false;
  }
}

/**
 * v038: «Fatto!» salva sul telefono e torna subito indietro; il conto si aggiorna in sottofondo (se non riesce, si
 * rimanda alla prossima apertura dell'app e l'errore va nel pannello).
 */
export function salvaAspettoESegui(avatar: number, colore: IdColore, suErrore: (e: unknown) => void): Promise<void> {
  salvaPreferenza(PREFERENZE.aspettoInSospeso, "1");
  aspettoInCorso = true;
  const fatto = salvaAspetto(avatar, colore)
    .then(() => salvaPreferenza(PREFERENZE.aspettoInSospeso, "0"))
    .catch(suErrore)
    .finally(() => (aspettoInCorso = false));
  // si aspetta al massimo un secondo e mezzo: con la rete lenta si torna indietro lo stesso
  return Promise.race([fatto, new Promise<void>((ok) => setTimeout(ok, 1500))]);
}

export async function salvaAspetto(avatar: number, colore: IdColore): Promise<void> {
  const conAvatar = eAvatar(avatar);
  if (conAvatar) {
    salvaPreferenza(PREFERENZE.avatar, String(avatar));
    avvisaAvatar();
  }
  salvaColore(colore);
  // l'account si sta ancora caricando (pagina appena aperta): si aspetta di sapere se c'è, poi si salva
  if (stato.tipo === "caricamento") await aggiornaIo().catch(() => undefined);
  if (stato.tipo !== "registrato") return;
  const io = stato.io;
  if (io.avatar === avatar && io.colore === colore) return; // niente di nuovo da mandare
  await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { colore, ...(conAvatar ? { avatar } : {}) } });
  imposta({ tipo: "registrato", io: { ...io, colore, ...(conAvatar ? { avatar } : {}) } });
}

const ascoltatoriAvatar = new Set<() => void>();
function avvisaAvatar(): void {
  for (const f of ascoltatoriAvatar) f();
}

/** Il numero dell'avatar da mostrare per me: quello dell'account, poi quello del telefono, poi il predefinito. */
export function avatarAttuale(s: StatoAccount = stato): number {
  if (s.tipo === "registrato" && eAvatar(s.io.avatar)) return s.io.avatar;
  const n = Number(leggiPreferenza(PREFERENZE.avatar));
  return eAvatar(n) ? n : AVATAR_PREDEFINITO;
}

/** L'avatar scelto da me (si aggiorna quando lo cambio o quando arriva il profilo). */
export function useAvatar(): number {
  const a = useAccount();
  const [, setVersione] = useState(0);
  useEffect(() => {
    const f = () => setVersione((v) => v + 1);
    ascoltatoriAvatar.add(f);
    return () => {
      ascoltatoriAvatar.delete(f);
    };
  }, []);
  return avatarAttuale(a);
}

/** C'è già un avatar scelto (sull'account o sul telefono)? */
export function avatarScelto(s: StatoAccount = stato): boolean {
  if (s.tipo === "registrato" && eAvatar(s.io.avatar)) return true;
  return eAvatar(Number(leggiPreferenza(PREFERENZE.avatar)));
}

export async function esci(ovunque = false): Promise<void> {
  try {
    await richiesta(ovunque ? "/api/v1/esci-ovunque" : "/api/v1/esci", { metodo: "POST" });
  } finally {
    salvaSessione(null);
    imposta({ tipo: "ospite" });
  }
}

export async function cancellaAccount(): Promise<void> {
  await richiesta("/api/v1/io", { metodo: "DELETE" });
  salvaSessione(null);
  salvaPreferenza(PREFERENZE.avatar, ""); // v019: l'avatar scelto sparisce con l'account (telefono condiviso)
  imposta({ tipo: "ospite" });
}

/** I miei dati in un file JSON da salvare. */
export async function scaricaDati(): Promise<void> {
  const dati = await richiesta<unknown>("/api/v1/io/dati");
  const blob = new Blob([JSON.stringify(dati, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "laughdown-dati.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Quota del giorno (per gli ospiti quella del telefono). */
export function leggiQuota(): Promise<Quota> {
  return richiesta<Quota>(`/api/v1/quota?dispositivo=${encodeURIComponent(idDispositivo())}`);
}

/** Lo sfidato registrato si fa riconoscere dopo essere entrato (partite valide, niente pubblicità se abbonato). */
export async function collegaSfida(codice: string, token: string): Promise<void> {
  if (stato.tipo !== "registrato") return;
  await richiesta(`/api/v1/sfide/${encodeURIComponent(codice)}/collega`, { metodo: "POST", corpo: { token } });
}

/** Nessuno dei due giocatori è abbonato? (deciso dal server: pubblicità e filtro a sorpresa) */
export async function nessunoAbbonato(codice: string, token: string): Promise<boolean> {
  // v045: la pubblicità dipende solo dal mio piano; il filtro a sorpresa da tutti e due (server vecchio: come prima)
  const r = await richiesta<{ pubblicita: boolean; nessunoAbbonato?: boolean }>(`/api/v1/sfide/${encodeURIComponent(codice)}/pubblicita`, { metodo: "POST", corpo: { token } });
  return r.nessunoAbbonato ?? r.pubblicita;
}

/** Si può mostrare la pubblicità a me? (v045: decide il mio piano, lo dice il server) */
export async function pubblicitaConsentita(codice: string, token: string): Promise<boolean> {
  const r = await richiesta<{ pubblicita: boolean }>(`/api/v1/sfide/${encodeURIComponent(codice)}/pubblicita`, {
    metodo: "POST",
    corpo: { token },
  });
  return r.pubblicita;
}

/**
 * Codice monouso per un video con premio (AdMob): l'app nativa lo passa al video come custom_data e Google lo
 * rimanda al server firmato; solo così il premio va a chi ha guardato il video. null = niente premi oggi.
 */
export async function codicePremio(): Promise<string | null> {
  const r = await richiesta<{ codice: string | null }>("/api/v1/premi/codice", { metodo: "POST", corpo: { dispositivo: idDispositivo() } });
  return r.codice;
}

// --- v021: spiegazione dell'analisi del volto (scelte legali 5 e 6) ------------------------------------------------
/**
 * Versione accettata (0 = mai). Con l'account conta quella dell'account (il consenso è della persona: un altro account
 * sullo stesso telefono la rivede, e se lo ritira scrivendoci la schermata ricompare); da ospite quella del telefono.
 */
function voltoVersioneAccettata(s: StatoAccount = stato): number {
  const n = s.tipo === "registrato" ? Number(s.io.voltoVersione ?? 0) : Number(leggiPreferenza(PREFERENZE.volto));
  return Number.isFinite(n) ? n : 0;
}
/**
 * v025: «Ho capito» dato in questa pagina (e da chi: l'id dell'account, o "ospite"). Vale subito, anche se il server
 * non ha ancora salvato (rete lenta, troppe richieste, localStorage che non scrive): prima la schermata ricompariva
 * all'infinito e non si andava avanti.
 */
let voltoQui: number | "ospite" | null = null;
/** Si può giocare senza rivedere la schermata? */
export function voltoAccettato(s: StatoAccount = stato): boolean {
  if (voltoQui !== null && (voltoQui === "ospite" || (s.tipo === "registrato" && s.io.id === voltoQui))) return true;
  return voltoVersioneAccettata(s) >= VERSIONE_VOLTO;
}
/** Ne aveva accettata una versione più vecchia (la schermata dice «abbiamo aggiornato questa spiegazione»). */
export function voltoAggiornato(s: StatoAccount = stato): boolean {
  const v = voltoVersioneAccettata(s);
  return v > 0 && v < VERSIONE_VOLTO;
}
/** «Ho capito, va bene»: vale subito, resta sul telefono e, con l'account, si salva sul server (in sottofondo, con
 * nuovi tentativi: la partita non aspetta il server). */
export async function accettaVolto(): Promise<void> {
  salvaPreferenza(PREFERENZE.volto, String(VERSIONE_VOLTO));
  if (stato.tipo !== "registrato") {
    voltoQui = "ospite";
    voltoDaSalvare = true;
    return;
  }
  voltoQui = stato.io.id;
  stato.io.voltoVersione = VERSIONE_VOLTO;
  void salvaVoltoSulServer();
}

async function salvaVoltoSulServer(tentativo = 0): Promise<void> {
  try {
    await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { voltoVersione: VERSIONE_VOLTO } });
  } catch {
    // rete giù o troppe richieste: si riprova fra poco (3 volte); poi vale lo stesso su questo telefono
    if (tentativo < 3) setTimeout(() => void salvaVoltoSulServer(tentativo + 1), 2000 * (tentativo + 1));
  }
}

/** v021 (scelta 8): la spunta «inizio subito e perdo il recesso» registrata sul server prima del pagamento sul sito. */
export function registraRinunciaRecesso(pacchetto: string, testo: string, lingua: string): Promise<{ id: number; il: string }> {
  return richiesta("/api/v1/io/rinuncia-recesso", { metodo: "POST", corpo: { pacchetto, testo, lingua } });
}

/** v021: il colore primario: subito sul telefono, poi (con l'account) sul server. */
export async function scegliColore(id: IdColore): Promise<void> {
  salvaColore(id);
  if (stato.tipo === "registrato") {
    await richiesta("/api/v1/io", { metodo: "PATCH", corpo: { colore: id } });
    stato.io.colore = id;
  }
}
